import { getApp } from 'firebase/app';
import {
  Auth,
  getMultiFactorResolver,
  GoogleAuthProvider,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  OAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithPopup,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { History } from 'history';
import * as React from 'react';
import { getErrorMessage } from '../../../../constants/firebase-auth-errors';
import { saveTenantConfig } from '../../../../data/providers/appsProvider';
import {
  createRecaptchaVerifier,
  getAuthForTenant,
  getUserOrganizations,
  sendEmailIdentification,
} from '../../../../data/providers/auth';
import { authenticate } from '../../../../data/providers/userProvider';
import {
  IFirebaseAppConfig,
  IIdentityTenant,
  IPermission,
  ISignInProvider,
  ISSOProvider,
} from '../../../../data/types';
import { sendPasswordReset } from '../../../../data/writers/userManagement';
import { getJsonField } from '../../../../utils/json';
import { LoginState } from '../Login';
import { signInWithSAMLPopup } from './samlBridge';

/**
 * Context object containing all state and functions needed by login handlers
 */
export interface LoginHandlerContext {
  // State values
  email: string;
  password: string;
  verificationCode: string;
  selectedFactor: MultiFactorInfo | null;
  selectedProvider: string | null;
  selectedSSOMethod: string | null;
  selectedTenant: string | null;
  selectedOrganization: string | null;

  // State setters
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
  setMultiFactorHints: (hints: MultiFactorInfo[]) => void;
  setResetSent: (sent: boolean) => void;

  // Refs
  userInfo: React.MutableRefObject<{
    projectId: string;
    sign_in_providers: ISignInProvider[];
    email: string;
    tenant_id?: string | null;
    org_id?: string;
    app_config?: IFirebaseAppConfig;
    organizations?: IPermission[];
    tenants?: IIdentityTenant[];
  } | null>;
  auth: React.MutableRefObject<Auth | null>;
  resolver: React.MutableRefObject<MultiFactorResolver | null>;
  verificationId: React.MutableRefObject<string | null>;
  recaptchaVerifier: React.MutableRefObject<RecaptchaVerifier | null>;

  // Functions
  nextState: (state: LoginState) => void;
  sendMultiFactorCode: () => Promise<void>;

  // Router
  history: History;
}

/**
 * Handler for IDENTIFY state - validates and looks up user email
 */
export const handleIdentifySubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  if (ctx.email.length === 0) {
    ctx.setError('Email is required.');
    return;
  }
  ctx.setLoading(true);

  // Use new auth flow: send email identification payload and get provider data
  try {
    ctx.userInfo.current = await sendEmailIdentification({
      email: ctx.email.trim().toLowerCase(),
      platform: 'web',
      app_id: 'dashboard',
    });
  } catch (err: unknown) {
    ctx.setLoading(false);

    // Handle identity service errors
    const error = err as Error;

    // Map error codes to user-friendly messages
    switch (error.message) {
      case 'USER_NOT_FOUND':
        ctx.setError('No account found with this email address.');
        break;
      case 'INVALID_REQUEST':
        ctx.setError(
          'Invalid email format. Please enter a valid email address.'
        );
        break;
      case 'APP_CHECK_FAILED':
        ctx.setError(
          'Unable to verify app authenticity. Please update to the latest version or contact support if the problem persists.'
        );
        break;
      case 'RATE_LIMIT_EXCEEDED':
        ctx.setError(
          'Too many login attempts. Please wait a moment and try again.'
        );
        break;
      case 'SERVICE_UNAVAILABLE':
        ctx.setError(
          'The authentication service is temporarily unavailable. Please try again in a few moments.'
        );
        break;
      case 'NETWORK_ERROR':
        ctx.setError(
          'Unable to connect to authentication service. Please check your internet connection or try again later.'
        );
        break;
      case 'SERVER_ERROR':
      case 'RESPONSE_PARSE_ERROR':
        ctx.setError(
          'An unexpected error occurred. Please try again or contact support if the problem persists.'
        );
        break;
      default:
        // For any other errors, show generic message
        ctx.setError('Unable to verify your email address. Please try again.');
        break;
    }
    return;
  }

  ctx.setLoading(false);

  // Check if user has multiple tenants - if so, show tenant selection
  if (
    ctx.userInfo.current?.tenants &&
    ctx.userInfo.current.tenants.length > 1
  ) {
    ctx.nextState(LoginState.SELECT_TENANT);
    return;
  }

  // Check if user has password provider
  const hasPassword = ctx.userInfo.current?.sign_in_providers?.some(
    (provider: ISignInProvider): boolean => provider.type === 'password'
  );

  // Check if user has SSO providers
  const ssoProviders = ctx.userInfo.current?.sign_in_providers?.filter(
    (provider) => provider.type === 'oidc' || provider.type === 'saml'
  );

  // If user has both password and SSO, or multiple SSO providers, show provider selection
  if (
    ctx.userInfo.current?.projectId &&
    ((hasPassword && ssoProviders && ssoProviders.length > 0) ||
      (ssoProviders && ssoProviders.length > 1))
  ) {
    ctx.nextState(LoginState.SELECT_PROVIDER);
  } else if (hasPassword) {
    // If only password is available, go to password authentication
    ctx.nextState(LoginState.AUTHENTICATE);
  } else if (ssoProviders && ssoProviders.length === 1) {
    // If only one SSO provider is available, automatically trigger SSO authentication
    await performSSOAuthentication(ctx, ssoProviders[0] as ISSOProvider);
  } else {
    // No providers available - this shouldn't happen, but fallback to password
    ctx.nextState(LoginState.AUTHENTICATE);
  }
};

/**
 * Handler for AUTHENTICATE state - performs password authentication
 */
export const handleAuthenticateSubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  ctx.setLoading(true);
  try {
    // Initialize Firebase Auth with config from identity service
    ctx.auth.current = getAuthForTenant(
      ctx.userInfo.current.app_config,
      ctx.userInfo.current.tenant_id ?? null
    );

    await authenticate(ctx.email.trim(), ctx.password.trim(), ctx.auth.current);

    // Save tenant config for auth persistence across page refreshes
    saveTenantConfig(
      ctx.userInfo.current.app_config,
      ctx.userInfo.current.tenant_id ?? null,
      ctx.userInfo.current.email
    );

    ctx.setLoading(false);
    ctx.history.push('/teams');
  } catch (err: unknown) {
    const authError = err as { code?: string };
    if (
      authError.code === 'auth/multi-factor-auth-required' ||
      authError.code === 'auth/verify-password-response'
    ) {
      ctx.setLoading(false);

      if (!ctx.auth.current) {
        ctx.setError(
          'Authentication configuration error. Please try again or contact support.'
        );
        ctx.setLoading(false);
        return;
      }

      try {
        // Use the auth instance directly to create MFA resolver
        ctx.resolver.current = getMultiFactorResolver(
          ctx.auth.current,
          err as unknown as MultiFactorError
        );

        ctx.setMultiFactorHints(ctx.resolver.current.hints);
        ctx.nextState(LoginState.SECOND_FACTOR_REQUIRED);

        /// can only call createRecaptchaVerifier once per page load
        if (!ctx.recaptchaVerifier.current) {
          ctx.recaptchaVerifier.current = createRecaptchaVerifier(
            'recaptcha-container'
          );
        }
      } catch (mfaError: unknown) {
        ctx.setError(
          'Unable to set up multi-factor authentication. Please try again or contact support.'
        );
        ctx.setLoading(false);
      }
    } else {
      console.error('Authentication error:', authError.code);
      ctx.setError(getErrorMessage(authError.code));
      ctx.setLoading(false);
    }
  }
};

/**
 * Handler for SECOND_FACTOR_REQUIRED state - sends MFA code
 */
export const handleSecondFactorRequiredSubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  await ctx.sendMultiFactorCode();
};

/**
 * Handler for SECOND_FACTOR_VERIFICATION state - verifies MFA code
 */
export const handleSecondFactorVerificationSubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  if (!ctx.selectedFactor) {
    ctx.setError('No authentication factor selected.');
    return;
  }

  ctx.setLoading(true);
  if (ctx.selectedFactor.factorId === 'phone') {
    const cred = PhoneAuthProvider.credential(
      ctx.verificationId.current,
      ctx.verificationCode
    );
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    try {
      await ctx.resolver.current.resolveSignIn(multiFactorAssertion);
      ctx.history.push('/teams');
    } catch (err: unknown) {
      const mfaError = err as { code?: string };
      ctx.setError(getErrorMessage(mfaError.code));
    }
    ctx.setLoading(false);
  } else {
    try {
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        ctx.selectedFactor.uid,
        ctx.verificationCode
      );
      await ctx.resolver.current.resolveSignIn(multiFactorAssertion);
      ctx.history.push('/teams');
    } catch (err: unknown) {
      const totpError = err as { code?: string };
      ctx.setError(getErrorMessage(totpError.code));
      ctx.setLoading(false);
    }
  }
};

/**
 * Handler for SELECT_PROVIDER state - navigates to selected auth method
 */
export const handleProviderSelectSubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  if (!ctx.selectedProvider) {
    ctx.setError('Please select a sign-in method.');
    return;
  }
  if (ctx.selectedProvider === 'password') {
    ctx.nextState(LoginState.AUTHENTICATE);
  } else if (ctx.selectedProvider === 'sso') {
    ctx.nextState(LoginState.SSO_AUTH);
  }
};

/**
 * Handler for SELECT_TENANT state - handles tenant selection for multi-tenant users
 */
export const handleTenantSelectSubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  if (!ctx.selectedTenant) {
    ctx.setError('Please select an organization.');
    return;
  }

  // Find the selected tenant from the tenants array
  const selectedTenantData = ctx.userInfo.current?.tenants?.find(
    (t: IIdentityTenant): boolean => t.org_id === ctx.selectedTenant
  );

  if (!selectedTenantData) {
    ctx.setError('Selected organization not found.');
    return;
  }

  // Update userInfo with selected tenant data
  if (ctx.userInfo.current) {
    // Extract app_config - it might be nested in a wrapper object or direct
    const rawAppConfig = selectedTenantData.app_config as any;
    const appConfig = rawAppConfig?.config || rawAppConfig;
    const projectId = getJsonField<string>(appConfig, 'projectId');

    ctx.userInfo.current = {
      projectId,
      email: ctx.userInfo.current.email,
      sign_in_providers: selectedTenantData.sign_in_providers,
      tenant_id: selectedTenantData.tenant_id,
      org_id: selectedTenantData.org_id,
      app_config: appConfig,
      tenants: ctx.userInfo.current.tenants,
    };
  }

  // Now proceed with provider selection logic
  const hasPassword = selectedTenantData.sign_in_providers?.some(
    (provider) => provider.type === 'password'
  );

  const ssoProviders = selectedTenantData.sign_in_providers?.filter(
    (provider) => provider.type === 'oidc' || provider.type === 'saml'
  );

  // If user has both password and SSO, or multiple SSO providers, show provider selection
  if (
    (hasPassword && ssoProviders && ssoProviders.length > 0) ||
    (ssoProviders && ssoProviders.length > 1)
  ) {
    ctx.nextState(LoginState.SELECT_PROVIDER);
  } else if (hasPassword) {
    // If only password is available, go to password authentication
    ctx.nextState(LoginState.AUTHENTICATE);
  } else if (ssoProviders && ssoProviders.length === 1) {
    // If only one SSO provider is available, automatically trigger SSO authentication
    await performSSOAuthentication(ctx, ssoProviders[0] as ISSOProvider);
  } else {
    // No providers available - fallback to password
    ctx.nextState(LoginState.AUTHENTICATE);
  }
};

/**
 * Handler for SELECT_ORGANIZATION state - handles organization selection
 */
export const handleOrganizationSelectSubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  if (!ctx.selectedOrganization) {
    ctx.setError('Please select an organization.');
    return;
  }

  // Redirect to teams page - the app will handle filtering by selected org
  ctx.history.push('/teams');
};

/**
 * Handler for FORGOT_PASSWORD state - sends password reset email
 */
export const handleForgotPasswordSubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  if (!ctx.userInfo.current?.app_config) {
    ctx.setError('Unable to send password reset. Please try again.');
    return;
  }

  ctx.setLoading(true);

  try {
    // Initialize the tenant app with the config we already have from identity lookup
    const tenantId = ctx.userInfo.current.tenant_id || null;
    const appName = tenantId
      ? `app-${tenantId}`
      : `app-${ctx.userInfo.current.projectId}`;

    getAuthForTenant(ctx.userInfo.current.app_config, tenantId);
    const app = getApp(appName);

    // Send the password reset email
    await sendPasswordReset(ctx.email, app, tenantId);

    ctx.setResetSent(true);
    ctx.setLoading(false);
  } catch (err: unknown) {
    const error = err as { code?: string };
    console.error('Password reset error:', error.code || 'unknown');
    ctx.setError('Unable to send password reset email. Please try again.');
    ctx.setLoading(false);
  }
};

/**
 * Performs SSO authentication with the given provider
 * Helper function used by both manual selection and auto-trigger flows
 */
const performSSOAuthentication = async (
  ctx: LoginHandlerContext,
  provider: ISSOProvider
): Promise<void> => {
  if (!ctx.userInfo.current?.projectId) {
    ctx.setError('Authentication session not initialized.');
    ctx.setLoading(false);
    return;
  }

  ctx.setLoading(true);

  try {
    // Get Firebase auth with tenant context
    ctx.auth.current = getAuthForTenant(
      ctx.userInfo.current.app_config,
      ctx.userInfo.current.tenant_id ?? null
    );

    let result;

    // Handle SAML differently from OIDC
    if (provider.type === 'saml') {
      // SAML: Use saml-bridge with custom token flow
      const samlBridgeDomain = getJsonField<string>(
        ctx.userInfo.current.app_config,
        'samlBridgeDomain'
      );
      if (!samlBridgeDomain) {
        ctx.setError(
          'SSO configuration error. Please contact techsupport@hawkindynamics.com'
        );
        ctx.setLoading(false);
        return;
      }

      result = await signInWithSAMLPopup(
        ctx.auth.current,
        ctx.email,
        ctx.userInfo.current.org_id || '',
        ctx.userInfo.current.tenant_id ?? null,
        provider.firebase_provider_id,
        samlBridgeDomain
      );
    } else if (provider.type === 'oidc') {
      // OIDC: Use Firebase native popup flow
      let oauthProvider: GoogleAuthProvider | OAuthProvider;

      if (provider.firebase_provider_id === 'google.com') {
        oauthProvider = new GoogleAuthProvider();
      } else if (provider.firebase_provider_id === 'microsoft.com') {
        oauthProvider = new OAuthProvider('microsoft.com');
      } else {
        oauthProvider = new OAuthProvider(provider.firebase_provider_id);
      }

      result = await signInWithPopup(ctx.auth.current, oauthProvider);
    } else {
      ctx.setError('Invalid SSO provider type.');
      ctx.setLoading(false);
      return;
    }

    // Save tenant config for auth persistence across page refreshes
    saveTenantConfig(
      ctx.userInfo.current.app_config,
      ctx.userInfo.current.tenant_id ?? null,
      ctx.userInfo.current.email
    );

    // Fetch user's organizations/teams
    try {
      const organizations = await getUserOrganizations(result.user.uid);

      // Store organizations in userInfo
      if (ctx.userInfo.current) {
        ctx.userInfo.current.organizations = organizations;
      }

      ctx.setLoading(false);

      // Get unique organization IDs to check if user has multiple orgs
      const uniqueOrgIds = new Set(organizations.map((perm) => perm.orgId));

      // If user has access to multiple organizations, show selection
      if (uniqueOrgIds.size > 1) {
        ctx.nextState(LoginState.SELECT_ORGANIZATION);
      } else {
        // Single organization or no organizations - proceed to teams page
        ctx.history.push('/teams');
      }
    } catch (orgError) {
      // If we can't fetch organizations, still allow login
      const error = orgError as { code?: string; message?: string };
      console.error('Error fetching organizations:', error.code || error.message || 'unknown');
      ctx.setLoading(false);
      ctx.history.push('/teams');
    }
  } catch (err: unknown) {
    const authError = err as { code?: string; message?: string };

    console.error('SSO authentication error:', authError.code || 'unknown');

    // Handle specific error cases
    if (authError.code === 'auth/account-exists-with-different-credential') {
      ctx.setError(
        'Account linking is not configured. Please contact your administrator.'
      );
    } else if (authError.code === 'auth/user-disabled') {
      ctx.setError(
        'Your account has been disabled. Please contact your administrator for assistance.'
      );
    } else if (authError.code === 'auth/operation-not-allowed') {
      const providerName = provider?.name || 'SSO';
      ctx.setError(
        `${providerName} sign-in is not enabled. Please contact your administrator to enable this sign-in method.`
      );
    } else if (authError.code === 'auth/popup-closed-by-user') {
      ctx.setError('Sign-in popup was closed. Please try again.');
    } else if (authError.code === 'auth/cancelled-popup-request') {
      ctx.setError('Sign-in was cancelled. Please try again.');
    } else if (authError.code === 'auth/unauthorized-domain') {
      ctx.setError(
        'This domain is not authorized for OAuth operations. Please contact your administrator.'
      );
    } else {
      ctx.setError(
        getErrorMessage(authError.code) ||
          authError.message ||
          'An error occurred during sign-in.'
      );
    }

    ctx.setLoading(false);
  }
};

/**
 * Handler for SSO_AUTH state - handles SSO authentication
 */
export const handleSSOAuthSubmit = async (
  ctx: LoginHandlerContext
): Promise<void> => {
  if (!ctx.selectedSSOMethod) {
    ctx.setError('Please select an SSO method.');
    return;
  }

  // Find the selected SSO provider from sign_in_providers
  const selectedProvider = ctx.userInfo.current?.sign_in_providers?.find(
    (p) =>
      p.type !== 'password' &&
      (p.id === ctx.selectedSSOMethod || p.name === ctx.selectedSSOMethod)
  ) as ISSOProvider | undefined;

  if (!selectedProvider) {
    ctx.setError('Selected SSO provider not found.');
    return;
  }

  await performSSOAuthentication(ctx, selectedProvider);
};

/**
 * Creates the submission handler strategy map
 */
export const createSubmissionHandlers = (
  ctx: LoginHandlerContext
): Record<LoginState, () => Promise<void>> => {
  return {
    [LoginState.IDENTIFY]: (): Promise<void> => handleIdentifySubmit(ctx),
    [LoginState.SELECT_TENANT]: (): Promise<void> =>
      handleTenantSelectSubmit(ctx),
    [LoginState.SELECT_PROVIDER]: (): Promise<void> =>
      handleProviderSelectSubmit(ctx),
    [LoginState.AUTHENTICATE]: (): Promise<void> =>
      handleAuthenticateSubmit(ctx),
    [LoginState.FORGOT_PASSWORD]: (): Promise<void> =>
      handleForgotPasswordSubmit(ctx),
    [LoginState.SECOND_FACTOR_REQUIRED]: (): Promise<void> =>
      handleSecondFactorRequiredSubmit(ctx),
    [LoginState.VERIFYING_DEVICE_FOR_MFA]: async (): Promise<void> => {
      // No-op: This is an intermediate loading state, no submission action
    },
    [LoginState.SECOND_FACTOR_VERIFICATION]: async (): Promise<void> =>
      handleSecondFactorVerificationSubmit(ctx),
    [LoginState.UNABLE_TO_SEND_VERIFICATION]: async (): Promise<void> => {
      // No-op: This is an error state with its own "Start Over" button
    },
    [LoginState.SSO_AUTH]: (): Promise<void> => handleSSOAuthSubmit(ctx),
    [LoginState.SELECT_ORGANIZATION]: (): Promise<void> =>
      handleOrganizationSelectSubmit(ctx),
  };
};
